import { Trans, useTranslation } from 'react-i18next'
import { CheckIcon, CopyIcon } from '@/components/atoms/icons'
import { referFriend } from '@/shared/assets'
import { ActionModal } from '../action-modal'
import { useReferModal } from './refer-modal.hook'

export interface ReferModalProps {
  isOpen: boolean
  onClose: () => void
}

function ReferIllustration() {
  return (
    <img
      src={referFriend}
      alt=""
      aria-hidden
      className="h-25.75 w-[10.09rem] self-start"
    />
  )
}

export function ReferModal({ isOpen, onClose }: ReferModalProps) {
  const { t } = useTranslation()
  const { referralCode, isLoading, copied, copyCode } = useReferModal()

  return (
    <ActionModal
      isOpen={isOpen}
      variant="normal"
      icon={ReferIllustration}
      title={`${t('home.refer.title')} ${t('home.refer.titleHighlight')}`}
      onClose={onClose}
      primaryAction={{ label: t('referModal.gotIt'), onClick: onClose }}
    >
      <p className="[&_strong]:text-coral-base leading-5 text-neutral-600 [&_strong]:font-bold">
        <Trans i18nKey="home.refer.description" />
      </p>

      <div className="flex flex-col gap-2" data-testid="refer-modal-code">
        <span className="tracking-base text-xs leading-4 text-neutral-600">
          {t('referModal.codeLabel')}
        </span>
        <div className="border-coral-300 bg-coral-50 flex h-14 items-center justify-between gap-4 rounded-lg border border-dashed pr-2 pl-4">
          {isLoading ? (
            <span className="skeleton h-6 w-32 rounded" />
          ) : referralCode ? (
            <span
              className="truncate text-xl leading-6 font-bold tracking-[0.15em] text-neutral-900"
              data-testid="refer-modal-code-value"
            >
              {referralCode}
            </span>
          ) : (
            <span className="text-neutral-base truncate text-sm leading-4.5">
              {t('referModal.unavailable')}
            </span>
          )}
          <button
            type="button"
            className="text-indigo-base tracking-base flex h-10 shrink-0 items-center gap-1 rounded-full px-3 text-xs leading-4 font-bold hover:bg-white disabled:opacity-40"
            onClick={copyCode}
            disabled={!referralCode}
            aria-live="polite"
            data-testid="refer-modal-copy-button"
          >
            {copied ? (
              <CheckIcon className="size-4" />
            ) : (
              <CopyIcon className="size-4" />
            )}
            {copied ? t('referModal.copied') : t('referModal.copy')}
          </button>
        </div>
      </div>
    </ActionModal>
  )
}
